import React, { useEffect, useMemo, useState } from 'react';
import { addDays, addMonths, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { enCA, fr } from 'date-fns/locale';
import i18n from '../i18n';
import { activitiesAPI, centersAPI, patientsAPI, usersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import AccessibleModal from '../components/common/AccessibleModal';

const ACTIVITY_CATALOG = [
  {
    category: 'stimulation_cognitive',
    label: 'Stimulation cognitive et mémoire',
    titles: [
      'Atelier mémoire',
      'Jeux de logique',
      'Mots croisés en groupe',
      'Scrabble adapté',
      'Quiz culturel',
      'Atelier lecture-discussion',
      'Atelier écriture de souvenirs',
      'Stimulation numérique (tablettes)',
    ],
  },
  {
    category: 'activites_physiques',
    label: 'Activités physiques adaptées',
    titles: [
      'Gym douce',
      'Tai-chi adapté',
      'Yoga sur chaise',
      'Marche encadrée',
      'Exercices équilibre et prévention des chutes',
      'Danse douce',
      'Étirements et mobilité',
      'Renforcement musculaire léger',
    ],
  },
  {
    category: 'arts_creativite',
    label: 'Arts et créativité',
    titles: [
      'Atelier peinture',
      'Atelier dessin',
      'Bricolage',
      'Artisanat textile',
      'Poterie légère',
      'Photographie souvenir',
      'Coloriage thérapeutique',
      'Création cartes et décorations',
    ],
  },
  {
    category: 'musique_spectacle',
    label: 'Musique et expression',
    titles: [
      'Chorale',
      'Chant en groupe',
      'Karaoké',
      'Écoute musicale commentée',
      'Musicothérapie',
      'Percussions douces',
      'Danse en ligne senior',
      'Scène ouverte',
    ],
  },
  {
    category: 'social_communautaire',
    label: 'Socialisation et vie communautaire',
    titles: [
      'Café-rencontre',
      'Groupe de discussion',
      'Jeux de société',
      'Club d’amitié',
      'Accueil nouveaux usagers',
      'Fête anniversaire du mois',
      'Activité intergénérationnelle',
      'Rencontre bénévoles-usagers',
    ],
  },
  {
    category: 'bien_etre_relaxation',
    label: 'Bien-être et relaxation',
    titles: [
      'Méditation guidée',
      'Respiration et relaxation',
      'Aromathérapie douce',
      'Atelier sommeil',
      'Auto-massage des mains',
      'Espace snoezelen',
      'Gestion du stress',
      'Pause bien-être',
    ],
  },
  {
    category: 'autonomie_vie_quotidienne',
    label: 'Autonomie et AVQ/AVD',
    titles: [
      'Atelier prévention des chutes à domicile',
      'Atelier sécurité à la maison',
      'Gestion des médicaments (éducation)',
      'Atelier budget simplifié',
      'Organisation des rendez-vous',
      'Usage téléphone intelligent',
      'Atelier transport en sécurité',
      'Atelier habiletés domestiques',
    ],
  },
  {
    category: 'nutrition_cuisine',
    label: 'Nutrition et cuisine',
    titles: [
      'Atelier cuisine santé',
      'Dégustation thématique',
      'Éducation nutritionnelle',
      'Hydratation et santé',
      'Planification repas simples',
      'Cuisine culturelle',
      'Atelier collations nutritives',
      'Prévention dénutrition',
    ],
  },
  {
    category: 'sante_prevention',
    label: 'Santé et prévention',
    titles: [
      'Atelier hypertension et diabète',
      'Atelier santé cardiaque',
      'Atelier santé mentale',
      'Prévention grippe et infections',
      'Atelier douleur chronique',
      'Sensibilisation médicaments',
      'Clinique dépistage (partenaire)',
      'Éducation premiers secours',
    ],
  },
  {
    category: 'spiritualite_culture',
    label: 'Spiritualité, culture et identité',
    titles: [
      'Célébration culturelle',
      'Atelier traditions et souvenirs',
      'Rencontre spirituelle',
      'Lecture de textes inspirants',
      'Atelier patrimoine local',
      'Projection documentaire',
      'Cercle de parole',
      'Sortie culturelle',
    ],
  },
  {
    category: 'exterieur_sorties',
    label: 'Sorties et activités extérieures',
    titles: [
      'Sortie parc',
      'Sortie musée',
      'Sortie bibliothèque',
      'Jardinage thérapeutique',
      'Marche plein air',
      'Sortie marché local',
      'Pique-nique saisonnier',
      'Visite communautaire',
    ],
  },
  {
    category: 'technologie_numerique',
    label: 'Technologie et inclusion numérique',
    titles: [
      'Initiation tablette',
      'Appels vidéo avec la famille',
      'Sécurité en ligne',
      'Messagerie électronique',
      'Jeux numériques cognitifs',
      'Photo/vidéo sur mobile',
      'Accès services en ligne',
      'Atelier IA simplifiée',
    ],
  },
  {
    category: 'famille_proches',
    label: 'Soutien aux proches et famille',
    titles: [
      'Rencontre proches aidants',
      'Atelier communication famille-usager',
      'Planification du répit',
      'Psychoéducation aidants',
      'Réunion de suivi familial',
      'Atelier ressources communautaires',
      'Groupe de parole aidants',
      'Séance questions-réponses clinique',
    ],
  },
  {
    category: 'evenements_saisonniers',
    label: 'Événements saisonniers et fêtes',
    titles: [
      'Fête de Noël',
      'Fête de fin d’année',
      'Célébration Pâques',
      'Fête nationale',
      'Fête d’Halloween',
      'Semaine des aînés',
      'Journée portes ouvertes',
      'Événement communautaire spécial',
    ],
  },
  {
    category: 'evaluation_suivi',
    label: 'Évaluation, suivi et plans individualisés',
    titles: [
      'Évaluation fonctionnelle périodique',
      'Révision plan d’intervention',
      'Rencontre interdisciplinaire',
      'Bilan de participation',
      'Suivi satisfaction usager',
      'Suivi incidents et sécurité',
      'Réunion coordination PAB-usager',
      'Réévaluation objectifs',
    ],
  },
];

const ACTIVITY_CATEGORY_OPTIONS = ACTIVITY_CATALOG.map(item => ({
  value: item.category,
  label: item.label,
}));

const statusBadgeClass = (status) => {
  if (status === 'scheduled') return 'badge badge-primary';
  if (status === 'completed') return 'badge badge-success';
  if (status === 'cancelled') return 'badge badge-danger';
  return 'badge badge-gray';
};

const enrollmentBadgeClass = (status) => {
  if (status === 'enrolled') return 'badge badge-primary';
  if (status === 'waitlisted') return 'badge badge-warning';
  if (status === 'attended') return 'badge badge-success';
  if (status === 'absent') return 'badge badge-danger';
  if (status === 'cancelled') return 'badge badge-gray';
  return 'badge';
};

const calendarStyleByStatus = (status, selected) => {
  const selectedStyle = selected
    ? { background: 'var(--primary-100)', border: '1px solid var(--primary-light)', color: 'var(--primary-dark)' }
    : null;
  if (selectedStyle) return selectedStyle;
  if (status === 'completed') return { background: 'var(--success-light)', border: '1px solid #86efac', color: '#166534' };
  if (status === 'cancelled') return { background: 'var(--danger-light)', border: '1px solid #fecaca', color: '#991b1b' };
  return { background: 'var(--primary-50)', border: '1px solid var(--primary-light)', color: 'var(--primary-dark)' };
};

const statusLabel = (status) => {
  const isFr = i18n.language.startsWith('fr');
  const labels = {
    scheduled: isFr ? 'Planifiée' : 'Scheduled',
    completed: isFr ? 'Complétée' : 'Completed',
    cancelled: isFr ? 'Annulée' : 'Cancelled',
  };
  return labels[status] || status;
};

const enrollmentStatusLabel = (status) => {
  const isFr = i18n.language.startsWith('fr');
  const labels = {
    enrolled: isFr ? 'Inscrit' : 'Enrolled',
    waitlisted: isFr ? 'Liste d’attente' : 'Waitlisted',
    attended: isFr ? 'Présent' : 'Attended',
    absent: isFr ? 'Absent' : 'Absent',
    cancelled: isFr ? 'Annulé' : 'Cancelled',
  };
  return labels[status] || status;
};

const ActivitiesPage = () => {
  const { user, hasRole } = useAuth();
  const canManage = hasRole('admin', 'manager');
  const canManageEnrollmentStatus = hasRole('admin', 'manager', 'staff');
  const canEnroll = hasRole('admin', 'manager', 'usager');
  const isAdmin = user?.role === 'admin';
  const isPatient = user?.role === 'usager';
  const locale = i18n.language.startsWith('fr') ? fr : enCA;

  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [centers, setCenters] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [activityCatalogData, setActivityCatalogData] = useState(ACTIVITY_CATALOG);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    dateFrom: '',
    dateTo: '',
  });
  const [viewMode, setViewMode] = useState('list');
  const [calendarDate, setCalendarDate] = useState(new Date());

  const [showCreate, setShowCreate] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [createReadOnly, setCreateReadOnly] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [assigningActivity, setAssigningActivity] = useState(null);
  const [assigningLoading, setAssigningLoading] = useState(false);
  const [openMenuActivityId, setOpenMenuActivityId] = useState(null);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    category: ACTIVITY_CATEGORY_OPTIONS[0].value,
    centerId: '',
    startsAt: '',
    endsAt: '',
    capacity: 20,
    waitlistCapacity: 50,
    location: '',
    notes: '',
  });

  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    patientId: '',
    notes: '',
  });
  const [assignForm, setAssignForm] = useState({
    staffId: '',
  });

  const selectedActivity = useMemo(
    () => activities.find(a => a.id === selectedActivityId) || null,
    [activities, selectedActivityId]
  );
  const catalogCategoryOptions = useMemo(
    () => activityCatalogData.map(item => ({ value: item.category, label: item.label })),
    [activityCatalogData]
  );

  const titlesByCategory = useMemo(() => {
    const map = {};
    for (const item of activityCatalogData) map[item.category] = item.titles || [];
    return map;
  }, [activityCatalogData]);

  const categoryOptions = useMemo(() => {
    const options = [...catalogCategoryOptions];
    const existingCategories = Array.from(new Set(activities.map(a => a.category).filter(Boolean)));
    for (const cat of existingCategories) {
      if (!options.find(o => o.value === cat)) {
        options.push({ value: cat, label: cat });
      }
    }
    if (createForm.category && !options.find(o => o.value === createForm.category)) {
      options.push({ value: createForm.category, label: createForm.category });
    }
    return options;
  }, [catalogCategoryOptions, activities, createForm.category]);

  const titleOptionsForSelectedCategory = useMemo(() => {
    const options = [...(titlesByCategory[createForm.category] || [])];
    if (createForm.title && !options.includes(createForm.title)) {
      options.push(createForm.title);
    }
    return options;
  }, [titlesByCategory, createForm.category, createForm.title]);

  const staffOptionsForSelectedActivity = useMemo(() => {
    if (!assigningActivity) return [];
    return staffOptions.filter((staff) => {
      const centerIds = Array.isArray(staff.center_ids) ? staff.center_ids : [];
      return centerIds.includes(assigningActivity.center_id) || staff.center_id === assigningActivity.center_id;
    });
  }, [assigningActivity, staffOptions]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(calendarDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [calendarDate]);

  const monthCells = useMemo(() => {
    const start = startOfWeek(startOfMonth(calendarDate), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(calendarDate), { weekStartsOn: 1 });
    const cells = [];
    let cursor = start;
    while (cursor <= end) {
      cells.push(cursor);
      cursor = addDays(cursor, 1);
    }
    return cells;
  }, [calendarDate]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const res = await activitiesAPI.getAll({
        status: filters.status || undefined,
        category: filters.category || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      });
      setActivities(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMeta = async () => {
    try {
      const jobs = [];
      if (isAdmin) jobs.push(centersAPI.getAll());
      else jobs.push(Promise.resolve({ data: [] }));

      if (canEnroll && !isPatient) jobs.push(patientsAPI.getAll({ limit: 500 }));
      else jobs.push(Promise.resolve({ data: [] }));

      if (canManage) jobs.push(usersAPI.getAll({ role: 'staff', limit: 500 }));
      else jobs.push(Promise.resolve({ data: { users: [] } }));

      const [centersRes, patientsRes, staffRes] = await Promise.all(jobs);
      setCenters(centersRes.data || []);
      setPatients(patientsRes.data || []);
      setStaffOptions(staffRes.data?.users || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadActivityTemplates = async () => {
    try {
      const res = await activitiesAPI.getTemplates();
      if (Array.isArray(res.data?.categories) && res.data.categories.length > 0) {
        setActivityCatalogData(res.data.categories);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadEnrollments = async (activityId) => {
    if (!activityId) {
      setEnrollments([]);
      return;
    }
    setLoadingEnrollments(true);
    try {
      const res = await activitiesAPI.getEnrollments(activityId);
      setEnrollments(res.data || []);
    } catch (err) {
      console.error(err);
      setEnrollments([]);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  useEffect(() => {
    loadActivities();
  }, [filters.status, filters.category, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    loadMeta();
    loadActivityTemplates();
  }, []);

  useEffect(() => {
    if (!selectedActivityId && activities.length > 0) {
      setSelectedActivityId(activities[0].id);
    }
  }, [activities, selectedActivityId]);

  useEffect(() => {
    loadEnrollments(selectedActivityId);
  }, [selectedActivityId]);
  useEffect(() => {
    const closeMenu = () => setOpenMenuActivityId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const activitiesForDay = (day) =>
    activities.filter(a => isSameDay(new Date(a.starts_at), day));

  const formatYmd = (date) => format(date, 'yyyy-MM-dd');

  const applyQuickPeriod = (period) => {
    const now = new Date();
    if (period === 'today') {
      const d = formatYmd(now);
      setFilters(f => ({ ...f, dateFrom: d, dateTo: d }));
      setViewMode('week');
      setCalendarDate(now);
      return;
    }
    if (period === 'week') {
      const from = startOfWeek(now, { weekStartsOn: 1 });
      const to = endOfWeek(now, { weekStartsOn: 1 });
      setFilters(f => ({ ...f, dateFrom: formatYmd(from), dateTo: formatYmd(to) }));
      setViewMode('week');
      setCalendarDate(now);
      return;
    }
    const from = startOfMonth(now);
    const to = endOfMonth(now);
    setFilters(f => ({ ...f, dateFrom: formatYmd(from), dateTo: formatYmd(to) }));
    setViewMode('month');
    setCalendarDate(now);
  };

  const goPrev = () => {
    if (viewMode === 'month') setCalendarDate(d => subMonths(d, 1));
    else if (viewMode === 'week') setCalendarDate(d => addDays(d, -7));
  };

  const goNext = () => {
    if (viewMode === 'month') setCalendarDate(d => addMonths(d, 1));
    else if (viewMode === 'week') setCalendarDate(d => addDays(d, 7));
  };

  const goToday = () => setCalendarDate(new Date());

  const toInputDatetime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  };

  const openActivityForm = (activity = null, readOnly = false) => {
    if (!activity) {
      setCreateForm({
        title: '',
        description: '',
        category: ACTIVITY_CATEGORY_OPTIONS[0].value,
        centerId: '',
        startsAt: '',
        endsAt: '',
        capacity: 20,
        waitlistCapacity: 50,
        location: '',
        notes: '',
      });
      setEditingActivityId(null);
      setCreateReadOnly(false);
      setShowCreate(true);
      return;
    }

    setCreateForm({
      title: activity.title || '',
      description: activity.description || '',
      category: activity.category || ACTIVITY_CATEGORY_OPTIONS[0].value,
      centerId: activity.center_id || '',
      startsAt: toInputDatetime(activity.starts_at),
      endsAt: toInputDatetime(activity.ends_at),
      capacity: activity.capacity || 20,
      waitlistCapacity: activity.waitlist_capacity || 50,
      location: activity.location || '',
      notes: activity.notes || '',
    });
    setEditingActivityId(activity.id);
    setCreateReadOnly(readOnly);
    setShowCreate(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (createReadOnly) return;
    try {
      const start = new Date(createForm.startsAt);
      const end = new Date(createForm.endsAt);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        alert('Dates invalides');
        return;
      }
      if (end <= start) {
        alert('La date de fin doit être après la date de début');
        return;
      }

      const payload = {
        ...createForm,
        description: createForm.description || null,
        location: createForm.location || null,
        notes: createForm.notes || null,
        centerId: isAdmin ? createForm.centerId : undefined,
        startsAt: new Date(createForm.startsAt).toISOString(),
        endsAt: new Date(createForm.endsAt).toISOString(),
        capacity: Number(createForm.capacity),
        waitlistCapacity: Number(createForm.waitlistCapacity),
      };
      let response;
      if (editingActivityId) response = await activitiesAPI.update(editingActivityId, payload);
      else response = await activitiesAPI.create(payload);
      setCreateForm({
        title: '',
        description: '',
        category: ACTIVITY_CATEGORY_OPTIONS[0].value,
        centerId: '',
        startsAt: '',
        endsAt: '',
        capacity: 20,
        waitlistCapacity: 50,
        location: '',
        notes: '',
      });
      setEditingActivityId(null);
      setCreateReadOnly(false);
      setShowCreate(false);
      if (!editingActivityId) {
        setFilters({ status: '', category: '', dateFrom: '', dateTo: '' });
        setViewMode('list');
      }
      await loadActivities();
      if (response?.data?.activity?.id) {
        setSelectedActivityId(response.data.activity.id);
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la création de l’activité');
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!selectedActivityId) return;

    try {
      const payload = { notes: enrollForm.notes || null };
      if (!isPatient) payload.patientId = enrollForm.patientId;

      await activitiesAPI.enroll(selectedActivityId, payload);
      setEnrollForm({ patientId: '', notes: '' });
      await Promise.all([loadActivities(), loadEnrollments(selectedActivityId)]);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de l’inscription');
    }
  };

  const updateEnrollmentStatus = async (enrollment, status) => {
    try {
      await activitiesAPI.updateEnrollmentStatus(selectedActivityId, enrollment.id, { status });
      await Promise.all([loadActivities(), loadEnrollments(selectedActivityId)]);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const updateActivityStatus = async (activity, status) => {
    try {
      await activitiesAPI.update(activity.id, { status });
      await loadActivities();
      if (selectedActivityId === activity.id) {
        await loadEnrollments(activity.id);
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de la mise à jour activité');
    }
  };

  const openAssignDialog = (activity) => {
    setAssigningActivity(activity);
    setAssignForm({ staffId: activity.assigned_staff_id || '' });
    setShowAssignModal(true);
  };

  const handleAssignStaff = async (e) => {
    e.preventDefault();
    if (!assigningActivity?.id || !assignForm.staffId) return;

    try {
      setAssigningLoading(true);
      await activitiesAPI.assignStaff(assigningActivity.id, { staffId: assignForm.staffId });
      setShowAssignModal(false);
      setAssigningActivity(null);
      setAssignForm({ staffId: '' });
      await loadActivities();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Erreur lors de l’assignation');
    } finally {
      setAssigningLoading(false);
    }
  };

  const renderWeeklyCalendar = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: 'var(--gray-200)' }}>
      {weekDays.map(day => {
        const dayActivities = activitiesForDay(day);
        const isToday = isSameDay(day, new Date());
        return (
          <div key={day.toISOString()} style={{ background: 'white', minHeight: 220 }}>
            <div style={{
              padding: '12px 12px 8px',
              borderBottom: '1px solid var(--gray-100)',
              background: isToday ? 'var(--primary-50)' : 'var(--gray-50)'
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>
                {format(day, 'EEE', { locale })}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: isToday ? 'var(--primary)' : 'var(--gray-800)' }}>
                {format(day, 'd')}
              </div>
            </div>
            <div style={{ padding: 8 }}>
              {dayActivities.map(activity => (
                <div
                  key={activity.id}
                  style={{
                    ...calendarStyleByStatus(activity.status, selectedActivityId === activity.id),
                    borderRadius: 6,
                    padding: '6px 8px',
                    marginBottom: 4,
                    cursor: 'pointer',
                    fontSize: 11,
                  }}
                  onClick={() => setSelectedActivityId(activity.id)}
                >
                  <div style={{ fontWeight: 600 }}>{activity.title}</div>
                  <div style={{ color: 'var(--gray-500)' }}>
                    {format(new Date(activity.starts_at), 'HH:mm')} - {format(new Date(activity.ends_at), 'HH:mm')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderMonthlyCalendar = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: 'var(--gray-200)' }}>
      {monthCells.map(day => {
        const dayActivities = activitiesForDay(day);
        const inMonth = isSameMonth(day, calendarDate);
        const isToday = isSameDay(day, new Date());
        return (
          <div key={day.toISOString()} style={{ background: 'white', minHeight: 150, opacity: inMonth ? 1 : 0.55 }}>
            <div style={{
              padding: '8px 10px',
              borderBottom: '1px solid var(--gray-100)',
              background: isToday ? 'var(--primary-50)' : 'var(--gray-50)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: isToday ? 'var(--primary)' : 'var(--gray-700)' }}>
                {format(day, 'd')}
              </div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase' }}>
                {format(day, 'EEE', { locale })}
              </div>
            </div>
            <div style={{ padding: 6 }}>
              {dayActivities.slice(0, 3).map(activity => (
                <div
                  key={activity.id}
                  onClick={() => setSelectedActivityId(activity.id)}
                  style={{
                    ...calendarStyleByStatus(activity.status, selectedActivityId === activity.id),
                    fontSize: 10,
                    borderRadius: 5,
                    padding: '4px 6px',
                    marginBottom: 4,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {format(new Date(activity.starts_at), 'HH:mm')} {activity.title}
                </div>
              ))}
              {dayActivities.length > 3 ? (
                <div style={{ fontSize: 10, color: 'var(--gray-500)', padding: '2px 4px' }}>
                  +{dayActivities.length - 3} autres
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Activités</h2>
          <p className="text-muted">{activities.length} activités</p>
        </div>
        <div className="flex gap-2">
          <button className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('list')}>Liste</button>
          <button className={`btn ${viewMode === 'week' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('week')}>Hebdo</button>
          <button className={`btn ${viewMode === 'month' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setViewMode('month')}>Mensuel</button>
          {canManage ? (
            <button className="btn btn-primary" onClick={() => {
              if (showCreate) {
                setShowCreate(false);
                setCreateReadOnly(false);
                setEditingActivityId(null);
              } else {
                openActivityForm(null, false);
              }
            }}>
              {showCreate ? 'Fermer' : '+ Nouvelle activité'}
            </button>
          ) : null}
        </div>
      </div>

      <div className="card card-form" style={{ marginBottom: 16 }}>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <div className="form-row" style={{ width: '100%', margin: 0 }}>
            <div className="form-group">
              <label className="form-label">Statut</label>
              <select className="form-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">Tous</option>
                <option value="scheduled">Planifiée</option>
                <option value="completed">Complétée</option>
                <option value="cancelled">Annulée</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Catégorie</label>
              <select className="form-select" value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}>
                <option value="">Toutes</option>
                {categoryOptions.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Du</label>
              <input type="date" className="form-input" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Au</label>
              <input type="date" className="form-input" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2" style={{ width: '100%' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => applyQuickPeriod('today')}>Aujourd'hui</button>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => applyQuickPeriod('week')}>Cette semaine</button>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => applyQuickPeriod('month')}>Ce mois</button>
          </div>
        </div>
      </div>

      {(viewMode === 'week' || viewMode === 'month') ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <span className="card-title">
              {viewMode === 'week'
                ? `${format(weekDays[0], 'd MMM', { locale })} - ${format(weekDays[6], 'd MMM yyyy', { locale })}`
                : format(calendarDate, 'MMMM yyyy', { locale })}
            </span>
            <div className="flex gap-2">
              <button className="btn btn-outline btn-sm" onClick={goPrev}>← Préc.</button>
              <button className="btn btn-outline btn-sm" onClick={goToday}>Aujourd'hui</button>
              <button className="btn btn-outline btn-sm" onClick={goNext}>Suiv. →</button>
            </div>
          </div>
          <div className="table-wrapper">
            {loading ? (
              <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
            ) : viewMode === 'week' ? renderWeeklyCalendar() : renderMonthlyCalendar()}
          </div>
        </div>
      ) : null}

      {showCreate && (canManage || createReadOnly) ? (
        <div className="card card-form" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>
            {createReadOnly ? 'Consulter activité' : editingActivityId ? 'Modifier activité' : 'Créer une activité'}
          </h3>
          <form onSubmit={handleCreate}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Titre *</label>
                <select
                  className="form-select"
                  value={createForm.title}
                  onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                  required
                  disabled={createReadOnly}
                >
                  <option value="">Sélectionner une activité</option>
                  {titleOptionsForSelectedCategory.map(title => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Catégorie</label>
                <select
                  className="form-select"
                  value={createForm.category}
                  onChange={e => {
                    const nextCategory = e.target.value;
                    setCreateForm(f => {
                      const validTitles = titlesByCategory[nextCategory] || [];
                      return {
                        ...f,
                        category: nextCategory,
                        title: validTitles.includes(f.title) ? f.title : '',
                      };
                    });
                  }}
                  disabled={createReadOnly}
                >
                  {categoryOptions.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Début *</label>
                <input type="datetime-local" className="form-input" value={createForm.startsAt} onChange={e => setCreateForm(f => ({ ...f, startsAt: e.target.value }))} required disabled={createReadOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">Fin *</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={createForm.endsAt}
                  min={createForm.startsAt || undefined}
                  onChange={e => setCreateForm(f => ({ ...f, endsAt: e.target.value }))}
                  required
                  disabled={createReadOnly}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Capacité *</label>
                <input type="number" min={1} className="form-input" value={createForm.capacity} onChange={e => setCreateForm(f => ({ ...f, capacity: e.target.value }))} required disabled={createReadOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">Capacité liste d’attente *</label>
                <input type="number" min={0} className="form-input" value={createForm.waitlistCapacity} onChange={e => setCreateForm(f => ({ ...f, waitlistCapacity: e.target.value }))} required disabled={createReadOnly} />
              </div>
            </div>
            {isAdmin ? (
              <div className="form-group">
                <label className="form-label">Centre *</label>
                <select className="form-select" value={createForm.centerId} onChange={e => setCreateForm(f => ({ ...f, centerId: e.target.value }))} required disabled={createReadOnly}>
                  <option value="">Sélectionner</option>
                  {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            ) : null}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Lieu</label>
                <input className="form-input" value={createForm.location} onChange={e => setCreateForm(f => ({ ...f, location: e.target.value }))} disabled={createReadOnly} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-input" value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} disabled={createReadOnly} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" rows={2} value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} disabled={createReadOnly} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {createReadOnly ? (
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    setCreateReadOnly(false);
                    setEditingActivityId(null);
                  }}
                >
                  Fermer
                </button>
              ) : (
                <button className="btn btn-primary" type="submit">{editingActivityId ? 'Modifier' : 'Créer'}</button>
              )}
            </div>
          </form>
        </div>
      ) : null}

      <div className="grid-2" style={{ gap: 24 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">📅 Activités planifiées</span>
          </div>
          <div className="table-wrapper">
            {loading ? (
              <div style={{ padding: 48, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Date</th>
                    <th>Capacité</th>
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!activities.length ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>Aucune activité</td></tr>
                  ) : activities.map(a => (
                    <tr
                      key={a.id}
                      onClick={() => setSelectedActivityId(a.id)}
                      style={{
                        background: selectedActivityId === a.id ? 'var(--primary-50)' : undefined,
                        cursor: 'pointer',
                      }}
                    >
                      <td>
                        <div style={{ fontWeight: 600 }}>{a.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{a.center_name || '—'}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                          Personnel: {a.assigned_staff_first_name ? `${a.assigned_staff_first_name} ${a.assigned_staff_last_name}` : 'Non assigné'}
                        </div>
                      </td>
                      <td>{new Date(a.starts_at).toLocaleString('fr-CA')}</td>
                      <td>
                        <div>{a.enrolled_count}/{a.capacity}</div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Attente: {a.waitlisted_count}/{a.waitlist_capacity}</div>
                      </td>
                      <td><span className={statusBadgeClass(a.status)}>{statusLabel(a.status)}</span></td>
                      <td>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuActivityId(curr => (curr === a.id ? null : a.id));
                            }}
                            aria-label="Ouvrir le menu d'actions"
                            aria-haspopup="menu"
                            aria-expanded={openMenuActivityId === a.id}
                          >
                            ⋮
                          </button>
                          {openMenuActivityId === a.id && (
                            <div
                              role="menu"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: 'absolute',
                                top: '110%',
                                right: 0,
                                minWidth: 170,
                                background: 'white',
                                border: '1px solid var(--gray-200)',
                                borderRadius: 8,
                                boxShadow: 'var(--shadow-md)',
                                zIndex: 20,
                                padding: 6,
                              }}
                            >
                              <button
                                className="btn btn-sm btn-outline"
                                style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                onClick={() => {
                                  setSelectedActivityId(a.id);
                                  openActivityForm(a, true);
                                  setOpenMenuActivityId(null);
                                }}
                              >
                                Consulter
                              </button>
                              {canManage && (
                                <button
                                  className="btn btn-sm btn-outline"
                                  style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                  onClick={() => {
                                    openActivityForm(a, false);
                                    setOpenMenuActivityId(null);
                                  }}
                                >
                                  Modifier
                                </button>
                              )}
                              {canManage && (
                                <button
                                  className="btn btn-sm btn-outline"
                                  style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 6 }}
                                  onClick={() => {
                                    openAssignDialog(a);
                                    setOpenMenuActivityId(null);
                                  }}
                                >
                                  Assigner
                                </button>
                              )}
                              {canManage && (
                                <button
                                  className="btn btn-sm btn-danger"
                                  style={{ width: '100%', justifyContent: 'flex-start' }}
                                  onClick={() => {
                                    setOpenMenuActivityId(null);
                                    updateActivityStatus(a, 'cancelled');
                                  }}
                                >
                                  Supprimer
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">👥 Inscriptions</span>
            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
              {selectedActivity ? selectedActivity.title : 'Sélectionnez une activité'}
            </span>
          </div>
          <div className="card-body">
            {canEnroll && activities.length > 0 ? (
              <form onSubmit={handleEnroll} style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Activité</label>
                  <select className="form-select" value={selectedActivityId} onChange={e => setSelectedActivityId(e.target.value)} required>
                    <option value="">Sélectionner</option>
                    {activities.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.title} - {new Date(a.starts_at).toLocaleString('fr-CA')}
                      </option>
                    ))}
                  </select>
                </div>
                {!isPatient ? (
                  <div className="form-group">
                    <label className="form-label">Usager</label>
                    <select className="form-select" value={enrollForm.patientId} onChange={e => setEnrollForm(f => ({ ...f, patientId: e.target.value }))} required>
                      <option value="">Sélectionner</option>
                      {patients.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                    </select>
                  </div>
                ) : null}
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input className="form-input" value={enrollForm.notes} onChange={e => setEnrollForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <button className="btn btn-primary" type="submit">Inscrire</button>
              </form>
            ) : (
              <div style={{ marginBottom: 16, color: 'var(--gray-500)' }}>Aucune activité disponible pour inscription.</div>
            )}

            <div className="table-wrapper">
              {loadingEnrollments ? (
                <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" style={{ margin: 'auto' }} /></div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Usager</th>
                      <th>Statut</th>
                      <th>Inscrit le</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!enrollments.length ? (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>Aucune inscription</td></tr>
                    ) : enrollments.map(e => (
                      <tr key={e.id}>
                        <td>{e.patient_first_name} {e.patient_last_name}</td>
                        <td><span className={enrollmentBadgeClass(e.status)}>{enrollmentStatusLabel(e.status)}</span></td>
                        <td>{e.enrolled_at ? new Date(e.enrolled_at).toLocaleString('fr-CA') : '—'}</td>
                        <td>
                          {canManageEnrollmentStatus ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button className="btn btn-sm btn-outline" onClick={() => updateEnrollmentStatus(e, 'attended')}>Présent</button>
                              <button className="btn btn-sm btn-outline" onClick={() => updateEnrollmentStatus(e, 'absent')}>Absent</button>
                              <button className="btn btn-sm btn-outline" onClick={() => updateEnrollmentStatus(e, 'cancelled')}>Annuler</button>
                              <button className="btn btn-sm btn-outline" onClick={() => updateEnrollmentStatus(e, 'enrolled')}>Réinscrire</button>
                            </div>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAssignModal && assigningActivity && (
        <AccessibleModal
          title="Assigner un personnel"
          onClose={() => {
            setShowAssignModal(false);
            setAssigningActivity(null);
            setAssignForm({ staffId: '' });
          }}
          footer={(
            <>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setShowAssignModal(false);
                  setAssigningActivity(null);
                  setAssignForm({ staffId: '' });
                }}
              >
                Annuler
              </button>
              <button className="btn btn-primary" type="submit" form="assign-staff-form" disabled={assigningLoading || !staffOptionsForSelectedActivity.length}>
                {assigningLoading ? 'Assignation...' : 'Assigner'}
              </button>
            </>
          )}
        >
            <form id="assign-staff-form" onSubmit={handleAssignStaff}>
                <div className="form-group">
                  <label className="form-label">Activité</label>
                  <input
                    className="form-input"
                    value={assigningActivity.title || ''}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Personnel</label>
                  <select
                    className="form-select"
                    value={assignForm.staffId}
                    onChange={(e) => setAssignForm({ staffId: e.target.value })}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {staffOptionsForSelectedActivity.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                {!staffOptionsForSelectedActivity.length ? (
                  <div className="text-muted">Aucun personnel disponible dans ce centre.</div>
                ) : null}
            </form>
        </AccessibleModal>
      )}
    </div>
  );
};

export default ActivitiesPage;
